import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CampaignDetail as CampaignDetailType } from '../../lib/types';
import { formatNumber, formatCurrency } from '../../lib/export';
import { Button } from '../../components/ui/button';
import { format } from 'date-fns';

export function CampaignDetail() {
  const { brandId, campaignId } = useParams<{ brandId: string; campaignId: string }>();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState<CampaignDetailType | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCampaignDetail();
  }, [brandId, campaignId]);

  const fetchCampaignDetail = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/brand/${brandId}/campaigns/${campaignId}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch campaign detail');
      }

      const data = await response.json();
      setCampaign(data.campaign);
    } catch (error) {
      console.error('Error fetching campaign detail:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg text-gray-600">Loading campaign...</div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg text-red-600">Campaign not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6 flex justify-between items-center">
          <div>
            <Button 
              variant="ghost" 
              onClick={() => navigate(`/brand/${brandId}/campaigns`)}
              className="mb-2"
            >
              ← Back to Campaigns
            </Button>
            <h1 className="text-3xl font-bold text-gray-900">{campaign.name}</h1>
            <p className="text-sm text-gray-500 mt-1">
              {format(new Date(campaign.startDate), 'MMM d, yyyy')} - {format(new Date(campaign.endDate), 'MMM d, yyyy')}
            </p>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Budget</h3>
            <p className="text-2xl font-bold text-gray-900">
              {campaign.budget ? formatCurrency(campaign.budget) : 'N/A'}
            </p>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Revenue</h3>
            <p className="text-2xl font-bold text-gray-900">
              {formatCurrency(campaign.revenue)}
            </p>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500 mb-2">ROAS</h3>
            <p className={`text-2xl font-bold ${
              campaign.roas !== null && campaign.roas > 1 ? 'text-green-600' : 'text-red-600'
            }`}>
              {campaign.roas !== null ? `${campaign.roas.toFixed(2)}x` : 'N/A'}
            </p>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Machines</h3>
            <p className="text-2xl font-bold text-gray-900">
              {formatNumber(campaign.machineCount)}
            </p>
          </div>
        </div>

        {/* Campaign Funnel */}
        <div className="bg-white p-6 rounded-lg shadow mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Campaign Funnel</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-blue-50 rounded">
              <p className="text-3xl font-bold text-blue-600">{formatNumber(campaign.funnel.impressions)}</p>
              <p className="text-sm text-gray-600 mt-1">Impressions</p>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded">
              <p className="text-3xl font-bold text-purple-600">{formatNumber(campaign.funnel.interactions)}</p>
              <p className="text-sm text-gray-600 mt-1">Interactions</p>
              <p className="text-xs text-gray-500 mt-1">
                {campaign.funnel.impressions > 0 
                  ? `${((campaign.funnel.interactions / campaign.funnel.impressions) * 100).toFixed(2)}%` 
                  : '0%'}
              </p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded">
              <p className="text-3xl font-bold text-green-600">{formatNumber(campaign.funnel.vends)}</p>
              <p className="text-sm text-gray-600 mt-1">Vends</p>
              <p className="text-xs text-gray-500 mt-1">
                {campaign.funnel.interactions > 0 
                  ? `${((campaign.funnel.vends / campaign.funnel.interactions) * 100).toFixed(2)}%` 
                  : '0%'}
              </p>
            </div>
            <div className="text-center p-4 bg-orange-50 rounded">
              <p className="text-3xl font-bold text-orange-600">{campaign.funnel.conversionRate.toFixed(2)}%</p>
              <p className="text-sm text-gray-600 mt-1">Overall Conversion</p>
              <p className="text-xs text-gray-500 mt-1">Impressions → Vends</p>
            </div>
          </div>
        </div>

        {/* Top Machines */}
        <div className="bg-white rounded-lg shadow mb-8">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900">Top Performing Machines</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Machine
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Site
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    City
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Impressions
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Vends
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Revenue
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {campaign.topMachines.map((machine) => (
                  <tr key={machine.machineId} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {machine.machineName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {machine.siteName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {machine.city}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                      {formatNumber(machine.impressions)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                      {formatNumber(machine.vends)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                      {formatCurrency(machine.revenue)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Performance Over Time - Placeholder for chart */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Performance Over Time</h2>
          <div className="h-64 flex items-center justify-center border-2 border-dashed border-gray-300 rounded">
            <p className="text-gray-500">Chart visualization coming soon (Recharts integration)</p>
          </div>
        </div>
      </div>
    </div>
  );
}

